import type {OptionFilterItem} from '@/modules/common/models/filters';
import type {ExtractPropsWithoutRef} from '@/types';

import {useCallback, useMemo} from 'react';

import {Collapsible} from '@/components/Collapsible';
import {CountBadge} from '@/components/CountBadge';
import {HelpTooltip} from '@/components/HelpTooltip';
import {FilterCheckbox} from '@/modules/common/components/FilterCheckbox/FilterCheckbox';

type Props = ExtractPropsWithoutRef<'div'> & {
  caption: React.ReactNode;
  options: Array<OptionFilterItem>;
  isCollapsable?: boolean;
  isDefaultExpanded?: boolean;
  canShowOptionCount?: boolean;
  canShowOptionBestPrice?: boolean;
  canShowOptionCountZero?: boolean;
  tooltip?: string;
  onChange: (params: {
    type: 'clear' | 'pop' | 'push';
    value: Array<string>;
    option?: string;
  }) => void;
};

export const OptionFilter: React.FunctionComponent<Props> = ({
  caption,
  options,
  canShowOptionCount = true,
  canShowOptionBestPrice = false,
  canShowOptionCountZero = false,
  onChange,
  isCollapsable,
  tooltip,
  isDefaultExpanded,
  ...restProps
}) => {
  const activeValues = useMemo(
    () =>
      options.filter((option) => option.active).map((option) => option.value),
    [options]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    ({currentTarget}) => {
      if (activeValues.includes(currentTarget.value)) {
        const nextValue = activeValues.filter(
          (value) => value !== currentTarget.value
        );
        if (nextValue.length === 0) {
          onChange({type: 'clear', value: []});
        } else {
          onChange({
            type: 'pop',
            value: nextValue,
            option: currentTarget.value,
          });
        }
      } else {
        onChange({
          type: 'push',
          value: activeValues.concat(currentTarget.value),
          option: currentTarget.value,
        });
      }
    },
    [onChange, activeValues]
  );

  return (
    <Collapsible
      {...restProps}
      isCollapsible={isCollapsable}
      isCollapsed={!isDefaultExpanded}
      headline={
        <>
          {caption}
          {tooltip && <HelpTooltip placement="bottom" content={tooltip} />}
          {activeValues.length > 0 && (
            <CountBadge count={activeValues.length} />
          )}
        </>
      }
    >
      {options.map((item) => (
        <FilterCheckbox
          key={item.value}
          canShowOptionCount={canShowOptionCount}
          canShowOptionBestPrice={canShowOptionBestPrice}
          canShowOptionCountZero={canShowOptionCountZero}
          onChange={handleChange}
          {...item}
        />
      ))}
    </Collapsible>
  );
};
